import { PolymerElement, html } from '@polymer/polymer/polymer-element';
import { KatapultWidget } from './katapult-widget';
import '../../progress-bar/progress-bar';
import '@polymer/paper-input/paper-input';
import '../../katapult-elements/katapult-button.js';
import { Path } from '../../../modules/Path.js';
import { SquashNulls } from '../../../modules/SquashNulls.js';

class KpiReportDownloader extends KatapultWidget {
  static get widgetTitleTemplate() {
    return html` <div>KPI Report Downloader</div> `;
  }

  static get bodyTemplate() {
    return html`
      <style>
        #downloadProgressBar {
          height: 10px;
          margin-top: 16px;
          --progress-background-color: var(--secondary-color);
        }
        #buttonContainer {
          display: flex;
          justify-content: center;
          margin-top: 20px;
        }
        #buttonContainer > katapult-button:not(:last-of-type) {
          margin-right: 10px;
        }
      </style>
      <div style="display: flex;">
        <paper-input
          id="startDateInput"
          type="date"
          value="[[today]]"
          label="Start date"
          style="flex: 1; margin-right: 10px;"
        ></paper-input>
        <paper-input id="startTimeInput" type="time" value="12:00" label="Start time" style="flex: 1; margin-left: 10px;"></paper-input>
      </div>
      <div style="display: flex;">
        <paper-input id="endDateInput" type="date" value="[[today]]" label="End date" style="flex: 1; margin-right: 10px;"></paper-input>
        <paper-input id="endTimeInput" type="time" value="12:00" label="End time" style="flex: 1; margin-left: 10px;"></paper-input>
      </div>
      <div id="buttonContainer">
        <katapult-button
          color="var(--secondary-color)"
          loading="[[generatingReport]]"
          on-click="downloadKpiReport"
          style="margin-right: 8px;"
          >Download KPI Report</katapult-button
        >
        <katapult-button
          color="var(--secondary-color)"
          loading="[[generatingSpreadsheet]]"
          on-click="downloadSpreadsheet"
          style="margin-right: 8px;"
          >Download KPI Data</katapult-button
        >
        <katapult-button color="var(--secondary-color)" loading="[[generatingBillable]]" on-click="downloadBillingExport"
          >Download Billing Report</katapult-button
        >
      </div>
      <progress-bar id="downloadProgressBar"></progress-bar>
    `;
  }

  static get properties() {
    return {
      today: {
        type: String,
        value: () => new Date().toISOString().split('T')[0]
      },
      headerContent: {
        type: Boolean,
        value: false
      }
    };
  }

  ready() {
    super.ready();
  }

  getDatesAndTimes() {
    let startDate = this.shadowRoot
      .querySelector('#startDateInput')
      .value.split('-')
      .map((num) => parseInt(num, 10));
    let startTime = this.shadowRoot
      .querySelector('#startTimeInput')
      .value.split(':')
      .map((num) => parseInt(num, 10));
    let endDate = this.shadowRoot
      .querySelector('#endDateInput')
      .value.split('-')
      .map((num) => parseInt(num, 10));
    let endTime = this.shadowRoot
      .querySelector('#endTimeInput')
      .value.split(':')
      .map((num) => parseInt(num, 10));
    return [
      new Date(startDate[0], startDate[1] - 1, startDate[2], startTime[0], startTime[1], 0, 0),
      new Date(endDate[0], endDate[1] - 1, endDate[2], endTime[0], endTime[1], 59, 999)
    ];
  }

  async downloadKpiReport() {
    this.generatingReport = true;
    let prog = this.shadowRoot.querySelector('#downloadProgressBar');
    prog.value = 0;

    const COMPANY_NAME = 'katapult';
    const [START_DATE, END_DATE] = this.getDatesAndTimes();

    // HARDCODE: for ppl-kws
    let actionsModel = await FirebaseWorker.ref(`photoheight/company_space/katapult/project_tracking/action_tracking/PPL_standard/actions`)
      .once('value')
      .then((s) => s.val());
    let actionIds = Object.keys(actionsModel);

    firebase
      .firestore()
      .collection(`companies/${COMPANY_NAME}/action_tracking`)
      .orderBy('timestamp')
      .where('timestamp', '>', START_DATE)
      .where('timestamp', '<', END_DATE)
      .get()
      .then(async (s) => {
        let actors = {};
        s.forEach((doc) => {
          let data = doc.data();
          // Skip the action if its not in PPL models
          if (!actionIds.includes(data.action_id)) return;
          // Otherwise, continue
          let oldValue = Path.get(actors, `${data.uid}.actions.${data.action_id}.count`) || 0;
          Path.set(actors, `${data.uid}.actions.${data.action_id}.count`, oldValue + 1);

          let jobArray = Path.get(actors, `${data.uid}.actions.${data.action_id}.jobs`) || [];
          if (jobArray.indexOf(data.job_id) < 0) jobArray.push(data.job_id);
          Path.set(actors, `${data.uid}.actions.${data.action_id}.jobs`, jobArray);

          let valObject = Path.get(actors, `${data.uid}.actions.${data.action_id}.vals`) || {};
          if (valObject[data.value] == undefined) valObject[data.value] = 0;
          valObject[data.value] = valObject[data.value] + 1;
          Path.set(actors, `${data.uid}.actions.${data.action_id}.vals`, valObject);
        });
        let totals = {};
        let actorOutput = [];
        for (const actor_id in actors) {
          await FirebaseWorker.ref(`user_info/${actor_id}`)
            .once('value')
            .then((s) => {
              let output = `${s.exists() ? s.val().email : '[' + actor_id + ']'}` + '\n';
              for (const [action_id, action] of Object.entries(actors[actor_id].actions)) {
                output += `    ${actionsModel[action_id].action_name}: ${action.count} (${action.jobs.join(', ')})\n`;
                for (const key in action.vals) {
                  output += `        [${key}] = ${action.vals[key]}\n`;
                }
                let actionName = actionsModel[action_id].action_name;
                if (actionName == 'Make Ready') {
                  for (const key in action.vals) {
                    let actionNameVal = `${actionName} (${key})`;
                    totals[actionNameVal] = !!totals[actionNameVal] ? totals[actionNameVal] + action.vals[key] : action.vals[key];
                  }
                } else if (actionName.includes('Permit')) {
                  if (actionName.includes('Required') || actionName.includes('Submitted')) continue;
                  let actionNameGroup;
                  if (['PennDOT', 'ROW', 'Misc.', 'Sidewalk'].includes(actionName.split(' ')[0]))
                    actionNameGroup = 'PPL Permits (PennDOT, ROW, Misc, Sidewalk)';
                  else if (['RR', 'Trans'].includes(actionName.split(' ')[0])) actionNameGroup = 'PPL Permits (RR, Trans Undercrossing)';
                  if (actionNameGroup)
                    totals[actionNameGroup] = !!totals[actionNameGroup] ? totals[actionNameGroup] + action.count : action.count;
                } else totals[actionName] = !!totals[actionName] ? totals[actionName] + action.count : action.count;
              }
              actorOutput.push(output + '\n');
            });
        }
        let finalOutput = '=== TOTALS ===\n\n';
        // Print totals
        for (const actionName in totals) finalOutput += `PPL ${actionName}: ${totals[actionName]}\n`;
        finalOutput += '\n=== USER BREAKDOWN ==\n\n';
        prog.value = 100;
        finalOutput += actorOutput.sort((a, b) => a.substr(0, 10).localeCompare(b.substr(0, 10))).join('');
        let link = document.createElement('a');
        let blob = new Blob([finalOutput], { type: 'text/csv' });
        link.href = URL.createObjectURL(blob);
        link.download = 'KpiReport.txt';
        link.click();
        this.generatingReport = false;
      });
  }

  async downloadSpreadsheet() {
    this.generatingSpreadsheet = true;
    let prog = this.shadowRoot.querySelector('#downloadProgressBar');
    prog.value = 0;

    const COMPANY_NAME = 'katapult';
    const [START_DATE, END_DATE] = this.getDatesAndTimes();

    const UID_LOOKUP = {};
    const JOB_LOOKUP = {};

    let debugStartTime = Date.now();
    firebase
      .firestore()
      .collection(`companies/${COMPANY_NAME}/action_tracking`)
      .orderBy('timestamp')
      .where('timestamp', '>', START_DATE)
      .where('timestamp', '<', END_DATE)
      .get()
      .then(async (s) => {
        let debugEndTime = Date.now();
        let logs = [
          'DATE, TIME, ACTION ID, ACTION NAME, USER EMAIL, VALUE SET, ACTION FOR, JOB NAME, NODE ID, HAS FEEDBACK, FEEDBACK STATUS'
        ];
        let length = s.docs.length;

        // console.log('QUERY PERFORMANCE');
        // console.log('time elapsed:', ((debugEndTime - debugStartTime) / 1000), ' | # of records:', length);
        // console.log('records-per-second:', length / ((debugEndTime - debugStartTime) / 1000));

        let counter = 0;
        for (const doc of s.docs) {
          let line = [];
          let data = doc.data();
          // Sanitize commas
          for (const key in data) if (typeof data[key] == 'string') data[key] = data[key].replaceAll(',', '[comma]');
          // Get uid from lookup or firebase
          let userEmail = UID_LOOKUP[data.uid];
          if (!userEmail) {
            userEmail = await FirebaseWorker.ref(`user_info/${data.uid}/email`)
              .once('value')
              .then((s) => s.val());
            UID_LOOKUP[data.uid] = userEmail;
          }
          // Get job names from lookup or firebase
          let jobName = JOB_LOOKUP[data.job_id];
          if (!jobName) {
            jobName = await FirebaseWorker.ref(`photoheight/job_permissions/${COMPANY_NAME}/jobs/${data.job_id}/name`)
              .once('value')
              .then((s) => s.val());
            JOB_LOOKUP[data.job_id] = jobName;
          }
          line = [
            new Date((SquashNulls(data, 'timestamp', 'seconds') || 0) * 1000).toLocaleString(),
            data.action_id,
            data.action_name,
            userEmail || '[unknown]',
            data.value || '[undefined]',
            data.type || 'node',
            jobName,
            data.node_id,
            !!data.feedback_ref ? 'yes' : 'no',
            data.status || 'none'
          ];
          logs.push(line.join(','));
          ++counter;
          prog.value = (counter / length) * 100;
        }
        let finalOutput = logs.join('\n');
        let link = document.createElement('a');
        let blob = new Blob([finalOutput], { type: 'text/csv' });
        link.href = URL.createObjectURL(blob);
        link.download = 'KpiSpreadsheet.csv';
        link.click();
        this.generatingSpreadsheet = false;
      });
  }

  async downloadBillingExport() {
    this.generatingBillable = true;
    let prog = this.shadowRoot.querySelector('#downloadProgressBar');
    prog.value = 0;

    const COMPANY_NAME = 'katapult';
    const [START_DATE, END_DATE] = this.getDatesAndTimes();

    firebase
      .firestore()
      .collection(`companies/${COMPANY_NAME}/action_tracking`)
      .orderBy('timestamp')
      .where('timestamp', '>', START_DATE)
      .where('timestamp', '<', END_DATE)
      .where('action_id', 'in', ['-Mf8poyI8WpDUSYQkg9s', '-Mf8poyq4xobxfX-5lp9'])
      .get()
      .then(async (s) => {
        let length = s.docs.length;
        let counter = 0;
        let actions = s.docs.map((doc) => Object.assign(doc.data(), { $key: doc.id }));

        let jobs = {};
        for (const action of actions) {
          if (jobs[action.job_id] === undefined)
            jobs[action.job_id] = {
              name:
                (await FirebaseWorker.ref(`photoheight/jobs/${action.job_id}/name`)
                  .once('value')
                  .then((s) => s.val())) || '[unknown]',
              actions: []
            };
          jobs[action.job_id].actions.push(action);
          ++counter;
          prog.value = (counter / length) * 50;
        }

        let userCache = {};
        let out = [];
        let jobEntries = Object.entries(jobs);
        counter = 0;
        length = jobEntries.length;
        for (const [jobId, job] of jobEntries) {
          let str = `${job.name} (${jobId})\n`;
          for (const action of job.actions) {
            str += `,${action.value}`;
            if (userCache[action.uid] === undefined)
              userCache[action.uid] =
                (await FirebaseWorker.ref(`user_info/${action.uid}/email`)
                  .once('value')
                  .then((s) => s.val())) || '[unknown]';
            str += `,${userCache[action.uid]}\n`;
          }
          str += `,${job.actions.reduce((acc, a) => acc + parseInt(a.value), 0)},<=TOTAL\n`;
          out.push(str);
          ++counter;
          prog.value = (0.5 + counter / length) * 100;
        }

        let finalOutput = out.join('\n');
        let link = document.createElement('a');
        let blob = new Blob([finalOutput], { type: 'text/csv' });
        link.href = URL.createObjectURL(blob);
        link.download = 'KpiBilling.csv';
        link.click();
        this.generatingBillable = false;
      });
  }
}
window.customElements.define('kpi-report-downloader', KpiReportDownloader);
